export class NewRelicBrowser {
  constructor(config, getCustomAttributes) {
    this.config = config;
    this.getCustomAttributes = getCustomAttributes;
    this.nrAgentInstance = null;
    this.nrAgentPromise = null;
    this.queue = [];
  }

  getNewRelicAgent() {
    if (this.nrAgentInstance) {
      return Promise.resolve(this.nrAgentInstance);
    }

    if (!this.nrAgentPromise) {
      this.nrAgentPromise = this.loadNewRelicAgent();
    }

    return this.nrAgentPromise;
  }

  async loadNewRelicAgent() {
    try {
      const [{ Agent }, { GenericEvents }] = await Promise.all([
        // const [{ Agent }, { GenericEvents }, { JSErrors }] = await Promise.all([
        import(/* webpackChunkName: "newrelic-browser-agent" */ '@newrelic/browser-agent/loaders/agent'),
        import(/* webpackChunkName: "newrelic-browser-agent" */ '@newrelic/browser-agent/features/generic_events'),
        // import(/* webpackChunkName: "newrelic-browser-agent" */ '@newrelic/browser-agent/features/jserrors'),
      ]);
      const agent = new Agent({
        ...this.config,
        exposed: false,
        // features: [GenericEvents, JSErrors],
        features: [GenericEvents],
      });

      this.nrAgentInstance = agent;
      this.flushQueue(agent);

      return this.nrAgentInstance;
    } catch (_error) {
      this.nrAgentPromise = null;
      return null;
    }
  }

  sanitizeOptions(options) {
    if (!options) {
      return {};
    }

    return Object.keys(options).reduce((sanitizedOptions, key) => {
      if (options[key] !== undefined) {
        sanitizedOptions[key] = options[key];
      }

      return sanitizedOptions;
    }, {});
  }

  getAttributes(properties) {
    return this.sanitizeOptions({
      ...properties,
      ...this.getCustomAttributes(),
    });
  }

  enqueueOrRun(call) {
    if (this.nrAgentInstance) {
      this.runCall(this.nrAgentInstance, call);
      return;
    }

    this.queue.push(call);
    this.getNewRelicAgent();
  }

  runCall(agent, call) {
    try {
      if (call.kind === 'event' && typeof agent.recordCustomEvent === 'function') {
        agent.recordCustomEvent(call.eventType, call.properties);
      }

      if (call.kind === 'action' && typeof agent.addPageAction === 'function') {
        agent.addPageAction(call.actionName, call.properties);
      }

      if (call.kind === 'error' && typeof agent.noticeError === 'function') {
        agent.noticeError(call.error, call.properties);
      }
    } catch (_error) {
      // New Relic support must not break feature behavior.
    }
  }

  flushQueue(agent) {
    for (const call of this.queue) {
      this.runCall(agent, call);
    }

    this.queue.length = 0;
  }

  recordEvent(eventType, properties) {
    this.enqueueOrRun({ kind: 'event', eventType, properties: this.getAttributes(properties) });
  }

  recordAction(actionName, properties) {
    this.enqueueOrRun({ kind: 'action', actionName, properties: this.getAttributes(properties) });
  }

  recordError(error, properties) {
    this.enqueueOrRun({ kind: 'error', error, properties: this.getAttributes(properties) });
  }
}
